import React, { useEffect, useState } from 'react';
import { branchService } from '../../services/branchService';
import { Branch } from '../../types/branch.types';
import Modal from '../../components/common/Modal';
import { useToast } from '../../context/ToastContext';
import AdminHeader from '../../components/admin/AdminHeader';

const BranchManagement: React.FC = () => {
    const { showToast } = useToast();
    const [branches, setBranches] = useState<Branch[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Form data
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        city: '',
        province: '',
        phoneNumber: '',
        email: ''
    });

    useEffect(() => {
        loadBranches();
    }, []);

    const loadBranches = async () => {
        try {
            setLoading(true);
            const data = await branchService.getAllBranches();
            setBranches(data);
        } catch (error) {
            console.error('Error loading branches:', error);
            showToast('Error al cargar sucursales', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBranch = async () => {
        setSubmitting(true);
        try {
            await branchService.createBranch(formData);
            showToast('Sucursal creada correctamente', 'success');
            setIsModalOpen(false);
            setFormData({
                name: '',
                address: '',
                city: '',
                province: '',
                phoneNumber: '',
                email: ''
            });
            loadBranches();
        } catch (error) {
            console.error('Error creating branch:', error);
            showToast('Error al crear sucursal', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <AdminHeader
                title="🏢 Gestión de Sucursales"
                rightContent={
                    <button className="btn btn-primary btn-sm" onClick={() => setIsModalOpen(true)}>
                        ➕ Nueva Sucursal
                    </button>
                }
            />
            <div className="container fade-in">

                {loading ? (
                    <div className="flex justify-center p-xl">
                        <div className="spinner"></div>
                    </div>
                ) : branches.length === 0 ? (
                    <div className="card text-center p-xl">
                        <p className="text-secondary mb-md">No hay sucursales registradas.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-md">
                        {branches.map(branch => (
                            <div key={branch.id} className="card shadow-sm border-l-4" style={{ borderLeftColor: branch.isActive ? 'var(--accent)' : '#ef4444' }}>
                                <div className="flex justify-between items-start mb-sm">
                                    <h3 className="m-0 font-bold">{branch.name}</h3>
                                    {branch.isActive ? (
                                        <span className="status-badge status-active">Activa</span>
                                    ) : (
                                        <span className="status-badge status-inactive">Inactiva</span>
                                    )}
                                </div>
                                <p className="text-secondary text-sm mb-xs">{branch.address}, {branch.city}</p>
                                <p className="text-secondary text-sm mb-md">{branch.province}</p>

                                <div className="text-sm border-t pt-sm mt-sm border-gray-100 bg-gray-50 rounded-b p-sm mx-minus-md">
                                    <p className="m-0 mb-xs">📞 {branch.phoneNumber || 'N/A'}</p>
                                    <p className="m-0">✉️ {branch.email || 'N/A'}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                <Modal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title="Nueva Sucursal"
                >
                    <div className="form-group mb-md">
                        <label className="form-label">Nombre *</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Ej: Sucursal Centro"
                            required
                        />
                    </div>
                    <div className="form-group mb-md">
                        <label className="form-label">Dirección *</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            placeholder="Ej: Av. San Martín 123"
                            required
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-md mb-md">
                        <div className="form-group">
                            <label className="form-label">Ciudad</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.city}
                                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Provincia</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.province}
                                onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                            />
                        </div>
                    </div>
                    <div className="form-group mb-md">
                        <label className="form-label">Teléfono</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.phoneNumber}
                            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                        />
                    </div>
                    <div className="form-group mb-lg">
                        <label className="form-label">Email</label>
                        <input
                            type="email"
                            className="form-input"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                    </div>

                    <div className="flex flex-col-reverse md:flex-row justify-end gap-sm mt-lg pt-md border-t">
                        <button className="btn btn-outline w-full md:w-auto" onClick={() => setIsModalOpen(false)} disabled={submitting}>
                            Cancelar
                        </button>
                        <button className="btn btn-primary w-full md:w-auto" onClick={handleCreateBranch} disabled={!formData.name || !formData.address || submitting}>
                            {submitting ? 'Guardando...' : 'Guardar Sucursal'}
                        </button>
                    </div>
                </Modal>
            </div>
        </>
    );
};

export default BranchManagement;

